"use client"

import { Accordion as AccordionPrimitive } from "@base-ui/react/accordion"
import { useReducedMotion, motion, type HTMLMotionProps } from "framer-motion"

import { cn } from "@/lib/utils"

const ACCORDION_HEIGHT_VAR = "--accordion-panel-height" as const
const ACCORDION_WIDTH_VAR = "--accordion-panel-width" as const

type AccordionPanelState = AccordionPrimitive.Panel.State
type AccordionPanelProps = AccordionPrimitive.Panel.Props

const panelOpenTransition = {
  height: { type: "spring" as const, bounce: 0, stiffness: 420, damping: 36 },
  opacity: { type: "tween" as const, duration: 0.2, ease: [0.4, 0, 0.2, 1] as const },
  pointerEvents: { duration: 0 },
} as const

const panelOpenTransitionReduced = {
  height: { duration: 0 },
  opacity: { duration: 0 },
  pointerEvents: { duration: 0 },
} as const

function AccordionMotionPanel({
  panelProps,
  state,
}: {
  panelProps: React.ComponentProps<"div"> & { ref?: React.Ref<HTMLDivElement> }
  state: AccordionPanelState
}) {
  const reduced = useReducedMotion()
  // Base sets `hidden` when closed; we animate with Framer instead and use aria-hidden.
  const { className, style, hidden, children, ref, ...rest } = panelProps
  void hidden

  const nextStyle: React.CSSProperties = { ...style }
  if (nextStyle) {
    delete (nextStyle as Record<string, string | number | undefined>)[ACCORDION_HEIGHT_VAR]
    delete (nextStyle as Record<string, string | number | undefined>)[ACCORDION_WIDTH_VAR]
  }

  return (
    <motion.div
      ref={ref as React.Ref<HTMLDivElement>}
      {...(rest as Omit<HTMLMotionProps<"div">, "ref" | "children">)}
      className={cn("overflow-x-hidden overflow-y-clip", className)}
      style={{ ...nextStyle, overflow: "hidden" }}
      initial={false}
      animate={
        state.open
          ? {
              height: "auto",
              opacity: 1,
              pointerEvents: "auto",
            }
          : {
              height: 0,
              opacity: 0,
              pointerEvents: "none",
            }
      }
      transition={reduced ? panelOpenTransitionReduced : panelOpenTransition}
      aria-hidden={!state.open}
      inert={state.open ? undefined : true}
    >
      {children}
    </motion.div>
  )
}

function Accordion({ className, ...props }: AccordionPrimitive.Root.Props) {
  return (
    <AccordionPrimitive.Root
      data-slot="accordion"
      className={cn(className)}
      {...props}
    />
  )
}

function AccordionItem({ className, ...props }: AccordionPrimitive.Item.Props) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn(className)}
      {...props}
    />
  )
}

function AccordionHeader({ className, ...props }: AccordionPrimitive.Header.Props) {
  return (
    <AccordionPrimitive.Header
      data-slot="accordion-header"
      className={cn("m-0 font-inherit text-inherit", className)}
      {...props}
    />
  )
}

function AccordionTrigger({ className, ...props }: AccordionPrimitive.Trigger.Props) {
  return (
    <AccordionPrimitive.Trigger
      data-slot="accordion-trigger"
      className={cn(className)}
      {...props}
    />
  )
}

function AccordionContent({ className, children, ...props }: AccordionPanelProps) {
  return (
    <AccordionPrimitive.Panel
      data-slot="accordion-content"
      keepMounted
      className={className}
      render={(panelProps, state) => (
        <AccordionMotionPanel panelProps={panelProps} state={state} />
      )}
      {...props}
    >
      {children}
    </AccordionPrimitive.Panel>
  )
}

export { Accordion, AccordionItem, AccordionHeader, AccordionTrigger, AccordionContent }
